/*------------------------------------------------------------------------------
   About      : What the VARIABLES / SEMANTIC / CAPPING indicators actually show
                (epic DA-697)

                The list previously read three booleans off `validationState`.
                That was wrong twice over:

                1. quill declares `validationState` as
                   `Record<string, { status, checkedAt, detail? }>` — a map of
                   recorded checks, not booleans. Comparing an object to
                   `true`/`false` is always false, so every row rendered
                   "pending" regardless of what had passed.
                2. **Nothing in quill ever writes that field.** It is a `json`
                   column with no seeded keys and no status vocabulary, so
                   waiting for it means showing "pending" forever.

                So the two checks that CAN be computed from the record are
                computed, and only the one that genuinely needs a server are
                left unknown:

                  VARIABLES — every `${…}` token resolves against the catalog,
                              and no `dateFormatSuffix` variable is used bare.
                              Same rule set quill enforces on write, evaluated
                              off the cached catalog.
                  CAPPING   — every variable constrained in `templateCriteria`
                              also appears as a token in the text. Otherwise a
                              value is capped for a sentence that never shows it.
                  SEMANTIC  — needs the LLM (DA-2401). Unknown unless a verdict
                              was recorded on the record.

                A recorded `validationState` entry always wins over the derived
                value: it came from the authoritative server check, this is a
                local approximation of it.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { InsightCatalog,
         InsightRecord,
         InsightValidationState }        from '../types/insightTypes'
import { criteriaVariablesMissingFromText,
         definitionFieldGaps,
         extractTokens,
         resolveToken }                  from './insightCatalogHelpers'

export type ValidationVerdict = 'PASS' | 'WARN' | 'FAIL' | 'UNKNOWN'

export type ValidationCheck = 'variables' | 'semantic' | 'capping'

export interface CheckResult {
  verdict : ValidationVerdict
  /** Why, when it is not a plain pass. Rendered in the tooltip. */
  detail ?: string
  /** True when this came off the record rather than being derived here. */
  recorded: boolean
}

export type InsightValidationSummary = Record<ValidationCheck, CheckResult>

/*------------------------------------------------------------------------------
   Reading a recorded check
------------------------------------------------------------------------------*/

/**
 * quill's `VALIDATION_STATUS` has exactly two members. Anything else means the
 * slot was not written by quill, so fall through to derivation rather than
 * guessing — mapping an unrecognised status onto PASS would claim a check that
 * never ran.
 */
function verdictFromStatus(status: string | undefined): ValidationVerdict {
  switch (String(status ?? '').trim().toUpperCase()) {
    case 'PASS' : return 'PASS'
    case 'FAIL' : return 'FAIL'
    default     : return 'UNKNOWN'
  }
}

/**
 * Turns a slot's `detail` into something renderable.
 *
 * `detail` is explicitly free-form and **not always a string** — quill stores
 * the validator's message list for `variables` and the model's verdict object
 * verbatim for `semantic` (`{ issues, model, reasoning, verdict }`). Printing it
 * straight would put `[object Object]` in the tooltip.
 */
export function describeCheckDetail(detail: unknown): string | undefined {
  if (detail === null || detail === undefined) return undefined
  if (typeof detail === 'string') return detail || undefined
  if (Array.isArray(detail)) return detail.map(String).filter(Boolean).join('; ') || undefined

  if (typeof detail === 'object') {
    const asRecord = detail as Record<string, unknown>
    // The semantic verdict object: prefer the model's own prose, then its issues.
    const parts = [
      typeof asRecord.reasoning === 'string' ? asRecord.reasoning : undefined,
      Array.isArray(asRecord.issues) && asRecord.issues.length
        ? asRecord.issues.map(String).join('; ')
        : undefined,
    ].filter(Boolean)
    if (parts.length) return parts.join(' — ')
  }
  return undefined
}

function recordedCheck(
  state : InsightValidationState | null | undefined,
  key   : ValidationCheck,
): CheckResult | undefined {
  const entry = state?.[key]
  // Guard the shape: it is a free-form `json` column, so a value written by
  // anything other than quill's controller could be any shape at all.
  if (!entry || typeof entry !== 'object') return undefined

  /* For `semantic`, the model's own three-valued verdict lives in `detail` while
     `status` can only carry PASS/FAIL. Prefer the richer one so a WARN is not
     flattened into one of the two extremes. */
  const fromDetail = key === 'semantic'
    ? (entry.detail as Record<string, unknown> | undefined)?.verdict
    : undefined

  const verdict = (typeof fromDetail === 'string' && ['PASS', 'WARN', 'FAIL'].includes(fromDetail))
    ? fromDetail as ValidationVerdict
    : verdictFromStatus(entry.status)

  if (verdict === 'UNKNOWN') return undefined

  return { verdict, detail : describeCheckDetail(entry.detail), recorded : true }
}

/*------------------------------------------------------------------------------
   Deriving the checks that do not need a server
------------------------------------------------------------------------------*/

/**
 * Token health, derived from the catalog.
 *
 * UNKNOWN rather than PASS for empty text: an insight with no template text has
 * not passed a token check, it has nothing to check. Showing green there would
 * claim more than is known.
 */
export function deriveVariablesCheck(
  insight : Pick<InsightRecord, 'templateString'>
          & Partial<Pick<InsightRecord,
              'templateCriteria' | 'pickUnitType' | 'pickUnitCount' |
              'compareUnitType' | 'compareUnitCount'>>,
  catalog : InsightCatalog | undefined,
): CheckResult {

  const text = insight.templateString ?? ''
  if (!text.trim()) return { verdict : 'UNKNOWN', detail : 'No template text yet', recorded : false }

  // Without a catalog nothing can be resolved, so the honest answer is unknown
  // — not a pass that happens to have checked nothing.
  if (!catalog) return { verdict : 'UNKNOWN', detail : 'Catalog not loaded', recorded : false }

  const tokens = extractTokens(text)

  const problems : string[] = []
  for (const raw of tokens) {
    const resolved = resolveToken(catalog, raw)
    if (!resolved.known) {
      problems.push(`\${${raw}} is not a known variable`)
    } else if (resolved.bareDateToken) {
      problems.push(`\${${raw}} needs a date format suffix`)
    }
  }

  /* A variable that reads an unset record field is just as broken as an unknown
     one, and the server's stateless check cannot see it — it never receives the
     record. Folded in here so the tile and the list dot agree with what a save
     will actually do. */
  for (const gap of definitionFieldGaps(insight, catalog)) {
    problems.push(`\${${gap.code}} needs ${gap.field} to be set`)
  }

  if (problems.length) {
    return { verdict : 'FAIL', detail : problems.join('; '), recorded : false }
  }

  return tokens.length
    ? { verdict : 'PASS', detail : `${tokens.length} variable${tokens.length === 1 ? '' : 's'} resolved`, recorded : false }
    : { verdict : 'PASS', detail : 'No variables used', recorded : false }
}

/**
 * The capping precondition this screen owns: a variable constrained in
 * `templateCriteria` should also be referenced in the text.
 *
 * No criteria is a PASS, not an UNKNOWN — there is nothing being capped, so
 * there is nothing that can be mismatched.
 */
export function deriveCappingCheck(
  insight : Pick<InsightRecord, 'templateCriteria' | 'templateString'>,
  catalog : InsightCatalog | undefined,
): CheckResult {

  const constrained = Object.keys(insight.templateCriteria ?? {})
  if (!constrained.length) {
    return { verdict : 'PASS', detail : 'No criteria to apply', recorded : false }
  }
  if (!catalog) return { verdict : 'UNKNOWN', detail : 'Catalog not loaded', recorded : false }

  const missing = criteriaVariablesMissingFromText(
    insight.templateCriteria, insight.templateString, catalog,
  )

  return missing.length
    ? {
        verdict  : 'WARN',
        detail   : `Constrained but never shown: ${missing.join(', ')}`,
        recorded : false,
      }
    : { verdict : 'PASS', detail : 'All constrained variables appear in the text', recorded : false }
}

/*------------------------------------------------------------------------------
   The summary the UI renders
------------------------------------------------------------------------------*/

export function deriveInsightValidation(
  insight : Pick<InsightRecord, 'templateString' | 'templateCriteria' | 'validationState'>,
  catalog : InsightCatalog | undefined,
): InsightValidationSummary {

  const state = insight.validationState

  return {
    // A recorded result wins: it came from the authoritative server check.
    variables : recordedCheck(state, 'variables') ?? deriveVariablesCheck(insight, catalog),
    capping   : recordedCheck(state, 'capping')   ?? deriveCappingCheck(insight, catalog),
    // Not derivable — it needs the LLM (DA-2401). Unknown unless recorded.
    semantic  : recordedCheck(state, 'semantic')
                ?? { verdict : 'UNKNOWN', detail : 'Not checked — run Validate on the insight', recorded : false },
  }
}

/**
 * Worst verdict across the three, for the list's single-column filter.
 *
 * **An unrun SEMANTIC check does not hold the summary back.** It is not
 * something the list can run — it needs the LLM, per record, from the edit
 * screen — and nothing currently persists its result, so requiring it here
 * made `PASS` unreachable for every row ever: a dead filter option that looked
 * like a working one.
 *
 * An unrun VARIABLES check does hold it back, because that is the primary
 * signal and its absence means nothing meaningful was assessed — an empty draft
 * must not report a clean bill of health just because it has no criteria to
 * contradict.
 *
 * The per-dot display is unaffected: semantic still shows honestly as not-run.
 * This is only the collapsed value the filter compares against.
 */
export function overallVerdict(summary: InsightValidationSummary): ValidationVerdict {
  const verdicts = [summary.variables.verdict, summary.capping.verdict, summary.semantic.verdict]

  if (verdicts.includes('FAIL')) return 'FAIL'
  if (verdicts.includes('WARN')) return 'WARN'
  if (summary.variables.verdict === 'UNKNOWN') return 'UNKNOWN'

  return 'PASS'
}

/*------------------------------------------------------------------------------
   Building the payload quill persists

   ⚠️ All three slots are **client-owned**. quill stores what the save carries,
   verbatim: it neither computes, derives nor second-guesses any of them, and it
   **replaces the object wholesale rather than merging** it.

   Two consequences that shape everything below:

   1. **What we send IS what is stored.** Omitting a slot clears it. That is the
      intended way to say "never checked" — the status vocabulary is `PASS|FAIL`
      only, so there is no in-band way to express "not run", and quill's own
      comment says an absent slot is what renders neutral.
   2. **Never fabricate a verdict.** Sending `PASS` for a check that did not run
      writes a green dot asserting something nobody verified. A check with no
      real answer is omitted, not defaulted.
------------------------------------------------------------------------------*/

/** quill's `VALIDATION_STATUS`. There is no WARN and no UNKNOWN. */
type SlotStatus = 'PASS' | 'FAIL'

export interface BuiltValidationState {
  validationState : InsightValidationState
  /** ISO timestamp, sent alongside as its own column. */
  lastValidation  : string
}

export interface SemanticOutcome {
  verdict   : ValidationVerdict
  reasoning?: string
  issues   ?: string[]
  model    ?: string
}

export interface BuildValidationStateInput {
  insight  : Pick<InsightRecord, 'templateString' | 'templateCriteria'>
  catalog ?: InsightCatalog
  /** The server's token verdict, when one was run against THIS exact text.
   *  Authoritative — the same validator quill itself used to run. */
  serverVariables ?: { valid : boolean; errors : string[] }
  /** The LLM result, when one was run against THIS exact text. */
  semantic ?: SemanticOutcome
  /** Injected so tests are deterministic. */
  checkedAt : string
}

/** WARN has no home in a two-member enum; only an outright FAIL is a failure.
 *  The true verdict still travels in `detail.verdict`, which is what the dots
 *  read in preference to `status`. */
function statusFor(verdict: ValidationVerdict): SlotStatus | undefined {
  if (verdict === 'UNKNOWN') return undefined
  return verdict === 'FAIL' ? 'FAIL' : 'PASS'
}

/**
 * Assembles the `validationState` to persist.
 *
 * `variables` and `capping` are derivable from the record, so they are present
 * whenever they have an answer. `semantic` is present only when the LLM actually
 * ran against this text — which is the honest encoding of a TPM bypass: the slot
 * is absent because nothing checked it, not `FAIL` as though a model rejected it.
 */
export function buildValidationState(input: BuildValidationStateInput): BuiltValidationState {
  const { insight, catalog, serverVariables, semantic, checkedAt } = input

  const state : InsightValidationState = {}

  /* Prefer the server's token verdict — it is the authoritative validator and
     catches per-type resolvability the local pass does not model. */
  if (serverVariables) {
    state.variables = {
      status    : serverVariables.valid ? 'PASS' : 'FAIL',
      checkedAt,
      detail    : serverVariables.errors?.length ? serverVariables.errors : undefined,
    }
  } else {
    const derived = deriveVariablesCheck(insight, catalog)
    const status  = statusFor(derived.verdict)
    if (status) state.variables = { status, checkedAt, detail : derived.detail }
  }

  const capping = deriveCappingCheck(insight, catalog)
  if (statusFor(capping.verdict)) {
    state.capping = {
      // A constrained variable the sentence never renders is a real defect, so
      // the WARN this check produces is stored as FAIL — the enum has no middle.
      status    : capping.verdict === 'PASS' ? 'PASS' : 'FAIL',
      checkedAt,
      detail    : capping.detail,
    }
  }

  if (semantic) {
    const status = statusFor(semantic.verdict)
    if (status) {
      state.semantic = {
        status,
        checkedAt,
        /* The model's object verbatim, per quill's documented shape for this
           slot — and it carries the three-valued verdict a PASS|FAIL status
           cannot express. */
        detail : {
          verdict   : semantic.verdict,
          reasoning : semantic.reasoning ?? '',
          issues    : semantic.issues ?? [],
          ...(semantic.model ? { model : semantic.model } : {}),
        },
      }
    }
  }

  return { validationState : state, lastValidation : checkedAt }
}
